/**
 * Environment Detection - Single Source of Truth
 * 
 * This module provides a centralized way to detect and handle environment
 * differences between production and staging. All environment-aware behavior
 * should use these utilities.
 * 
 * Used by: UI components, Automation, Edge Functions, Audit systems
 */

export type Environment = 'production' | 'staging';

/**
 * Determines the current environment based on hostname and env vars.
 * Priority: hostname check first, then VITE_ENVIRONMENT fallback
 */
export function getEnvironment(): Environment {
    if (typeof window !== 'undefined') {
        const hostname = window.location.hostname;

        // Explicit staging hostname checks
        if (
            hostname === 'staging.propellertravel.com' ||
            hostname.includes('staging') ||
            hostname.includes('preview')
        ) {
            return 'staging';
        }
    }

    // Environment variable fallback
    if (import.meta.env.VITE_ENVIRONMENT === 'staging') {
        return 'staging';
    }

    return 'production';
}

/** Current environment - evaluated once at module load */
export const ENVIRONMENT = getEnvironment();

/** Check if currently running in staging environment */
export const isStaging = (): boolean => ENVIRONMENT === 'staging';

/** Check if currently running in production environment */
export const isProduction = (): boolean => ENVIRONMENT === 'production';

/**
 * Get environment-specific label for UI components
 */
export function getEnvironmentLabel(): string {
    return ENVIRONMENT === 'staging'
        ? '🧪 STAGING ENVIRONMENT'
        : 'Production';
}

/**
 * Get environment-specific styling class names
 */
export function getEnvironmentClasses(): string {
    return ENVIRONMENT === 'staging'
        ? 'bg-amber-500 text-amber-900'
        : '';
}
